import React, { useEffect, useRef, useState } from "react";

import { useSelector, useDispatch } from "react-redux";

import {
  setSelectedConversation,
  sortOngoingConvsStart,
  fetchMoreConversations,
  setSelectedConversations,
} from "redux/chats/chats.actions";
import {
  UserListHeader,
  UserListFilter,
  UserItem,
} from "components/Conversations";
import useTranslation from "hooks/useTranslation";
import useMediaQuery from "use-mediaquery";
import { ThreeDots } from "react-loader-spinner";
import { NoConvIcon } from "assets/icons";
import { use100vh } from "react-div-100vh";
import ProgressBar from "components/ProgressBar";
import UserItemSkeleton from "./components/UserItemSkeleton";
import useConvsListProgressData from "hooks/useConvsListProgressData";

const getConversationsDataOnType = ({ chatsData }) => ({
  pending: {
    loading: chatsData.pending.loading,
    totalCount: chatsData.pending.totalCount,
    data: chatsData.pending.data,
    hasMoreToLoad: chatsData.pending.hasMoreToLoad,
  },
  ongoing: {
    loading: chatsData.ongoing.loading,
    totalCount: chatsData.ongoing.totalCount,
    data: chatsData.ongoing.data,
    hasMoreToLoad: chatsData.ongoing.hasMoreToLoad,
  },
  otherConvs: {
    loading: chatsData.otherConvs.loading,
    totalCount: chatsData.otherConvs.totalCount,
    data: chatsData.otherConvs.data,
    hasMoreToLoad: chatsData.otherConvs.hasMoreToLoad,
  },
});

const getSelectedConversations = ({ chatsData }) =>
  chatsData.selectedConversations;

const getSelectedConversation = ({ chatsData }) =>
  chatsData.selectedConversation;

const getUnreadPendingMessage = ({ chatsData }) =>
  chatsData.unreadPendingMessage;

const getUnreadOngoingMessage = ({ chatsData }) =>
  chatsData.unreadOngoingMessage;

const getContainer = ({ userData }) => userData.container.data;

const getBlockedUsersList = ({ chatsData }) => chatsData.blockedUsers;

const UserList = ({ setConvBodyContent, toggleSettings }) => {
  const [searchingMessage, setSearchingMessage] = useState("");
  const [selectedDepartmentFilter, setSelectedDepartmentFilter] = useState("");

  const conversationsDataOnType = useSelector(getConversationsDataOnType);

  const blockedUsersList = useSelector(getBlockedUsersList);
  const selectedConversations = useSelector(getSelectedConversations);
  const selectedConversation = useSelector(getSelectedConversation);
  const unreadOngoingMessage = useSelector(getUnreadOngoingMessage);
  const unreadPendingMessage = useSelector(getUnreadPendingMessage);

  const container = useSelector(getContainer);
  const appHeight = use100vh();
  const listContainerRef = useRef();

  const alertsHeight = document.getElementById("alerts")?.clientHeight || 0;
  const convListInnerStyle = { height: `${appHeight - 220 - alertsHeight}px` };

  const { getTranslation } = useTranslation(["CHAT", "COMMON"]);
  const { t } = getTranslation();

  const dispatch = useDispatch();

  const [isLoadingMoreStatus, setIsLoadingMoreStatus] = useState({
    pending: false,
    ongoing: false,
    otherConvs: false,
  });

  const selectedConvType = selectedConversations;

  const [sortOrder, setSortOrder] = useState("descending"); // ascending | descending
  const isSmallDevice = useMediaQuery("(max-width: 650px)");

  const isLoading = conversationsDataOnType[selectedConvType].loading;
  const selecteConvTypeHasMoreToLoad =
    conversationsDataOnType[selectedConvType].hasMoreToLoad;
  const selectedConvsTypeData = conversationsDataOnType[selectedConvType].data;

  const [scrollProgress, remainingItems] = useConvsListProgressData({
    listContainerRef,
    selectedConvType,
    pendingConversations: conversationsDataOnType.pending.data,
    ongoingConversations: conversationsDataOnType.ongoing.data,
    otherOperatorsConversations: conversationsDataOnType.otherConvs.data,
  });

  const isSearchResultChat = (chats) =>
    chats?.some((c) => c.content.includes(searchingMessage));

  const handleClickSortBtn = (newSort) => {
    if (
      selectedConvType === "ongoing" &&
      conversationsDataOnType.ongoing.hasMoreToLoad
    ) {
      dispatch(sortOngoingConvsStart(newSort));
    }
    setSortOrder(newSort);
  };

  const setSelectedConvType = (convType) => {
    dispatch(setSelectedConversations(convType));
  };

  const sortConvsByUpdateDate = (convs = []) => {
    let sortedConvs = [];
    if (!convs) return sortedConvs;

    if (selecteConvTypeHasMoreToLoad) {
      return convs;
    }

    // sort like a messenger
    if (sortOrder === "descending")
      sortedConvs = convs.sort(function (a, b) {
        return new Date(b.updatedAtSort) - new Date(a.updatedAtSort);
      });
    else
      sortedConvs = convs.sort((a, b) => {
        return new Date(a.updatedAtSort) - new Date(b.updatedAtSort);
      });

    return sortedConvs;
  };

  const renderConvsListSkeleton = <UserItemSkeleton num={4} />;

  const renderMoreBtnLoader = (
    <div className="flex w-100 flex-jc-c">
      <ThreeDots
        height="23"
        width="35"
        radius="12"
        color="#b10e84"
        ariaLabel="three-dots-loading"
        wrapperStyle={{}}
        wrapperClassName=""
        visible={true}
      />
    </div>
  );

  const handleClickMoreOption = (loading) => {
    const skip = selectedConvsTypeData?.length;

    if (loading) return;

    setIsLoadingMoreStatus((prev) => ({ ...prev, [selectedConvType]: true }));

    dispatch(
      fetchMoreConversations({
        skip,
        convType: selectedConvType,
        setLoading: (newStatus) =>
          setIsLoadingMoreStatus((prev) => ({
            ...prev,
            [selectedConvType]: newStatus,
          })),
      })
    );
  };

  const renderLoadMoreSection = () => {
    const loadingMore = isLoadingMoreStatus[selectedConvType];

    const showMoreButton = selecteConvTypeHasMoreToLoad;

    if (showMoreButton)
      return (
        <div
          className="conv__list__inner__fetch_more_messages_container"
          data-disabled={loadingMore}
          data-show={remainingItems < 1 && scrollProgress === 100} // make it "true" when the list reached to the end.
          onClick={() => handleClickMoreOption(loadingMore)}
        >
          <span>{loadingMore ? renderMoreBtnLoader : t("SHOW_MORE")}</span>
        </div>
      );
  };

  const checkIsUserBlocked = (user) => {
    if (!user) return;

    const { id } = user;

    const userBlockStatus = blockedUsersList.find((item) => item.userId === id);

    return userBlockStatus;
  };

  const renderNoConvPlaceholder = (
    <div className="flex w-100 flex-jc-c flex-column flex-ai-c h-100">
      <img src={NoConvIcon} className="No_Conv_Icon" alt="No_Conv_Icon" />
      <p className="text-hint">{t("THERE_IS_NO_CONV")}</p>
    </div>
  );

  const renderConvsList = () => {
    // filter by department
    const convsFilteredByDepartment =
      selectedConvsTypeData && selectedDepartmentFilter
        ? selectedConvsTypeData.filter(
            (item) => item.teamId === selectedDepartmentFilter
          )
        : selectedConvsTypeData;

    // whether filtered or original convs list is filtered then return empty placeholder
    if (
      !selectedConvsTypeData?.length ||
      convsFilteredByDepartment.length === 0
    ) {
      return renderNoConvPlaceholder;
    }

    const convsList = sortConvsByUpdateDate(convsFilteredByDepartment).map(
      (conversation) =>
        conversation &&
        (!searchingMessage ||
          (searchingMessage &&
            (isSearchResultChat(conversation?.conversation?.chats) ||
              String(conversation?.user?.fullName).includes(
                searchingMessage
              )))) && (
          <UserItem
            key={conversation?.id}
            id={conversation?.id}
            selectedConversations={selectedConvType}
            setConvBodyContent={setConvBodyContent}
            selectedConversation={selectedConversation}
            packageType={container?.package?.type}
            blockStatus={checkIsUserBlocked(conversation.user)}
            inChatWithBot={conversation.inChatWithBot}
            convType={conversation.convType}
            {...conversation}
          />
        )
    );

    return [...convsList];
  };

  // clear selected chat when showing convs list
  useEffect(() => {
    if (isSmallDevice) {
      dispatch(setSelectedConversation(null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSmallDevice]);

  return (
    <>
      <UserListHeader
        setSearchingMessage={setSearchingMessage}
        searchingMessage={searchingMessage}
        setConvBodyContent={setConvBodyContent}
        toggleSettings={toggleSettings}
        setSelectedConvType={setSelectedConvType}
      />
      <UserListFilter
        selectedDepartmentFilter={selectedDepartmentFilter}
        setSelectedDepartmentFilter={setSelectedDepartmentFilter}
        selectedConversations={selectedConvType}
        unreadOngoingMessage={unreadOngoingMessage}
        unreadPendingMessage={unreadPendingMessage}
        setSelectedConvType={setSelectedConvType}
        sortOrder={sortOrder}
        setSortOrder={handleClickSortBtn}
      />

      <div
        style={convListInnerStyle}
        className="conv__list__inner"
        ref={listContainerRef}
        data-has-more-item={selecteConvTypeHasMoreToLoad}
      >
        {isLoading && !selectedConvsTypeData?.length
          ? renderConvsListSkeleton
          : renderConvsList()}
      </div>

      {selecteConvTypeHasMoreToLoad && (
        <div className="progress-bar-container" data-show={remainingItems > 0}>
          <ProgressBar
            progress={scrollProgress}
            trackWidth={5}
            indicatorWidth={5}
            size={56}
            style={{ position: "absolute" }}
          />
          <span>{remainingItems}</span>
        </div>
      )}
      {renderLoadMoreSection()}
    </>
  );
};

export default UserList;
